// Authentication API endpoints
import apiClient, { ApiError, setToken, removeToken } from './apiClient';
import { plainApiMessage } from '@/lib/apiErrorMessages';
import { toE164Phone } from '@/lib/e164Phone';
import type {
  LoginRequest,
  LoginResponse,
  MemberRegisterRequest,
  VenueRegisterRequest,
  RegisterResponse,
  VerifyPhoneRequest,
  User,
} from '@/types/api';

// Helper to extract data from wrapped response
// Handles various API response formats:
// - { token, user } - direct response
// - { data: { token, user } } - wrapped response
// - { success: true, token, user } - with success flag
// - { success: true, data: { token, user } } - wrapped with success flag
function unwrapResponse<T>(response: unknown): T {
  if (response === null || response === undefined) {
    return response as T;
  }

  if (typeof response !== 'object') {
    return response as T;
  }

  const obj = response as Record<string, unknown>;

  // Check for error response
  if ('success' in obj && obj.success === false) {
    const message = (obj.message as string) || 'Request failed';
    throw new Error(message);
  }

  // If response has a 'data' property, unwrap it
  if ('data' in obj && obj.data !== undefined) {
    // Check for double-wrapped: { data: { data: ... } }
    const innerData = obj.data as Record<string, unknown>;
    if (typeof innerData === 'object' && innerData !== null && 'data' in innerData) {
      return innerData.data as T;
    }
    return obj.data as T;
  }

  // Return as-is (may have token/user at top level with success: true)
  return response as T;
}

/** Where the phone OTP went — WhatsApp when the Twilio WhatsApp sender is configured, else SMS. */
export type VerificationChannel = 'sms' | 'whatsapp';

export type ParsedRegisterResponse = RegisterResponse & {
  requiresEmailVerification: boolean;
};

function userEmailIsVerified(user: User): boolean {
  return !!(
    user.emailVerifiedAt ||
    (user as unknown as { email_verified_at?: string | null }).email_verified_at
  );
}

/**
 * Register endpoints may return `success: false` with `data.action === "verify"`,
 * user, and token — that means the account was created and email verification is required.
 */
export function parseRegisterResponse(raw: unknown): ParsedRegisterResponse {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid registration response');
  }

  const obj = raw as Record<string, unknown>;
  const message =
    typeof obj.message === 'string' ? plainApiMessage(obj.message) : 'Registration failed';

  const dataObj =
    obj.data && typeof obj.data === 'object' ? (obj.data as Record<string, unknown>) : null;

  const action = dataObj?.action as string | undefined;
  const user = (dataObj?.user ?? obj.user) as User | undefined;
  const token = (dataObj?.token ?? obj.token) as string | undefined;

  const isVerifyRegistration =
    action === 'verify' || (obj.success === false && Boolean(user && token));

  if (isVerifyRegistration && user && token) {
    setToken(token);
    return {
      user,
      token,
      requiresEmailVerification: !userEmailIsVerified(user),
    };
  }

  if (obj.success === false) {
    throw new Error(message);
  }

  const unwrapped = unwrapResponse<RegisterResponse>(raw);
  if (unwrapped.token) setToken(unwrapped.token);
  return {
    ...unwrapped,
    requiresEmailVerification: !userEmailIsVerified(unwrapped.user),
  };
}

/** Recover verify-pending registration from an HTTP error body (4xx with same JSON shape). */
export function tryParseRegisterResponseFromApiError(
  err: unknown
): ParsedRegisterResponse | null {
  if (!(err instanceof ApiError) || !err.data) return null;
  try {
    return parseRegisterResponse(err.data);
  } catch {
    return null;
  }
}

export const authApi = {
  /**
   * Login with email/phone and password
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const endpoint = 'challenge' in credentials ? '/login/two-factor' : '/login';
    const rawResponse = await apiClient.post<LoginResponse | { data: LoginResponse }>(endpoint, credentials);
    const response = unwrapResponse<LoginResponse>(rawResponse);
    if (response.token) {
      setToken(response.token);
    } else {
      console.warn('[AuthAPI] No token in response!');
    }
    return response;
  },

  /** Re-text the SMS code for a pending 2FA sign-in. */
  resendTwoFactorCode: async (
    challenge: string,
    channel: 'sms' | 'whatsapp' | 'email',
  ): Promise<{ message?: string }> => {
    return apiClient.post('/login/two-factor/resend', { challenge, channel });
  },

  /**
   * Member email flow: notify backend to send / process verification for this email
   * (POST /member/check-email)
   */
  memberCheckEmail: async (data: { email: string }): Promise<{ message?: string }> => {
    return apiClient.post('/member/check-email', data);
  },

  /**
   * Register a new member account
   */
  registerMember: async (data: MemberRegisterRequest): Promise<ParsedRegisterResponse> => {
    const payload: MemberRegisterRequest = {
      ...data,
      phone: toE164Phone(data.phone),
    };
    const rawResponse = await apiClient.post<unknown>('/member/register', payload);
    return parseRegisterResponse(rawResponse);
  },

  /**
   * Register a new venue account
   */
  registerVenue: async (data: VenueRegisterRequest): Promise<ParsedRegisterResponse> => {
    const rawResponse = await apiClient.post<unknown>('/venue/register', { ...data, sourceSite: 'listaa' });
    return parseRegisterResponse(rawResponse);
  },

  /**
   * Verify phone number with OTP code
   */
  verifyPhone: async (data: VerifyPhoneRequest): Promise<{ message: string }> => {
    return apiClient.post('/verify-phone', data);
  },

  /**
   * Get social OAuth redirect URL
   */
  getSocialAuthUrl: async (provider: 'google' | 'apple'): Promise<{ url: string }> => {
    const rawResponse = await apiClient.get<{ url: string } | { data: { url: string } }>(`/social-oauth/${provider}`);
    return unwrapResponse<{ url: string }>(rawResponse);
  },

  /**
   * Complete social OAuth callback
   */
  socialAuthCallback: async (
    provider: 'google' | 'apple',
    code: string
  ): Promise<LoginResponse> => {
    const rawResponse = await apiClient.post<LoginResponse | { data: LoginResponse }>(
      `/social-oauth/${provider}/callback?code=${code}`,
      { code }
    );
    const response = unwrapResponse<LoginResponse>(rawResponse);
    if (response.token) {
      setToken(response.token);
    }
    return response;
  },

  /**
   * Get current user info
   */
  getCurrentUser: async (): Promise<User> => {
    const rawResponse = await apiClient.get<User | { data: User }>('/member', { requireAuth: true });
    return unwrapResponse<User>(rawResponse);
  },

  /**
   * Logout - clear token
   */
  logout: (): void => {
    removeToken();
  },

  /**
   * Activate account from email link (POST /verify-email).
   * Public — no auth header; invalid/expired links return success: false with 200.
   */
  verifyEmailActivate: async (data: {
    action: string;
    token: string;
    email: string;
  }): Promise<{ success: boolean; message?: string; user?: User; token?: string }> => {
    const raw = await apiClient.post<unknown>('/verify-email', data, { omitAuth: true });
    if (!raw || typeof raw !== 'object') {
      return { success: false, message: 'Invalid verification response.' };
    }
    const obj = raw as Record<string, unknown>;
    const message =
      typeof obj.message === 'string' ? plainApiMessage(obj.message) : undefined;
    if (obj.success === false) {
      return { success: false, message: message || 'Verification failed.' };
    }
    const dataObj =
      obj.data && typeof obj.data === 'object'
        ? (obj.data as Record<string, unknown>)
        : null;
    const user = (dataObj?.user ?? obj.user) as User | undefined;
    const token = (dataObj?.token ?? obj.token) as string | undefined;
    if (token) setToken(token);
    return { success: true, message, user, token };
  },

  /**
   * Resend email verification link (backend: POST /send-verify-email)
   */
  resendEmailVerification: async (id: string): Promise<{ message?: string } | unknown> => {
    return apiClient.post<{ message?: string }>(
      '/send-verify-email',
      { id },
      { requireAuth: true }
    );
  },

  /**
   * Resend phone/SMS verification (backend: POST …/send-verify-sms, same body as email resend)
   */
  resendVerifySms: async (id: string, channel?: VerificationChannel): Promise<{ message?: string; channel?: VerificationChannel }> => {
    return apiClient.post<{ message?: string; channel?: VerificationChannel }>(
      '/send-verify-sms',
      { id, channel },
      { requireAuth: true }
    );
  },

  /**
   * Request password reset email (public)
   */
  forgotPassword: async (data: { email: string }): Promise<unknown> => {
    return apiClient.post<unknown>("/forgot", data, { omitAuth: true });
  },

  /**
   * Reset password from email link (public). Logs the user in on success.
   */
  resetPassword: async (data: {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
  }): Promise<LoginResponse> => {
    const rawResponse = await apiClient.post<LoginResponse | { data: LoginResponse }>(
      "/reset",
      data,
      { omitAuth: true },
    );
    const response = unwrapResponse<LoginResponse>(rawResponse);
    if (response.token) {
      setToken(response.token);
    }
    return response;
  },

  /**
   * Check if current user's email is verified.
   * NOTE: Prefer AuthContext.checkEmailVerified() which picks the right endpoint per account type.
   * This fallback uses /member and is only valid for member accounts.
   */
  checkEmailVerified: async (): Promise<boolean> => {
    const rawResponse = await apiClient.get<User | { data: User }>('/member', { requireAuth: true });
    const u = unwrapResponse<User>(rawResponse);
    return !!(u.emailVerifiedAt || (u as unknown as { email_verified_at?: string }).email_verified_at);
  },
};

export default authApi;
